import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { 
  useGetAdministratorsQuery,
  useCreateAdministratorMutation,
  useUpdateAdministratorMutation,
  useDeleteAdministratorMutation,
} from '../../store/api/adminApi';
import { AdminRole } from '../../types/admin';

const AdminManagementTab: React.FC = () => {
  const { data: administrators, isLoading, error } = useGetAdministratorsQuery();
  const [createAdmin] = useCreateAdministratorMutation();
  const [updateAdmin] = useUpdateAdministratorMutation();
  const [deleteAdmin] = useDeleteAdministratorMutation();

  const [open, setOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: AdminRole.VIEWER,
  });

  const handleSubmit = async () => {
    try {
      if (editingAdmin) {
        await updateAdmin({
          id: editingAdmin.id,
          data: {
            name: formData.name,
            role: formData.role,
            isActive: true,
          },
        }).unwrap();
      } else {
        // パスワード検証
        if (formData.password.length < 8) {
          alert('パスワードは8文字以上で入力してください');
          return;
        }
        
        await createAdmin({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          permissions: {
            users: { read: true, verify: false, delete: false, ban: false },
            products: { read: true, delete: false, feature: false },
            chats: { read: true, moderate: false },
            reports: { read: true, resolve: false },
            analytics: { read: true },
            settings: { read: false, write: false },
          },
        }).unwrap();
      }
      setOpen(false);
      setFormData({ name: '', email: '', password: '', role: AdminRole.VIEWER });
      setEditingAdmin(null);
    } catch (error) {
      console.error('管理者操作に失敗しました:', error);
    }
  };

  const handleEdit = (admin: any) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      role: admin.role,
    });
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('この管理者を削除しますか？')) {
      try {
        await deleteAdmin(id).unwrap();
      } catch (error) {
        console.error('管理者削除に失敗しました:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        管理者データの読み込みに失敗しました
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          管理者管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          新規管理者追加
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名前</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>権限</TableCell>
              <TableCell>最終ログイン</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {administrators?.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>{admin.name}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>
                  <Chip
                    label={admin.role}
                    color={admin.role === AdminRole.SUPER_ADMIN ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : '未ログイン'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(admin)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(admin.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAdmin ? '管理者編集' : '新規管理者追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="名前"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="メールアドレス"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editingAdmin}
              fullWidth
            />
            {!editingAdmin && (
              <TextField
                label="初期パスワード"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                helperText="管理者が初回ログイン時に使用するパスワード（最低8文字）"
                fullWidth
                required
              />
            )}
            <FormControl fullWidth>
              <InputLabel>権限</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminRole })}
              >
                <MenuItem value={AdminRole.VIEWER}>閲覧者</MenuItem>
                <MenuItem value={AdminRole.SUPPORT}>サポート</MenuItem>
                <MenuItem value={AdminRole.MODERATOR}>モデレーター</MenuItem>
                <MenuItem value={AdminRole.SUPER_ADMIN}>スーパー管理者</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAdmin ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagementTab; 